/**
 * Seguimiento de aperturas y clics por campaña (Instantly `open_tracking` / `link_tracking`).
 * Lógica compartida entre el worker (que lo guarda) y la API (que calcula las tasas).
 */

export interface TrackingState {
  on: boolean | null;
  since: Date | null;
}

/**
 * Próximo estado al ver la campaña en Instantly:
 *  - apagado → `since` se borra (si se vuelve a prender, cuenta desde ese momento);
 *  - prendido y antes apagado → desde ahora;
 *  - prendido y nunca visto → desde que se creó la campaña (se asume que siempre midió);
 *  - prendido y ya prendido → no cambia.
 */
export function nextTrackingState(
  local: TrackingState,
  remoteOn: boolean | null | undefined,
  createdAt: Date | null,
  now: Date,
): TrackingState {
  if (remoteOn === undefined || remoteOn === null) return local;
  if (!remoteOn) return { on: false, since: null };
  if (local.on === true) return { on: true, since: local.since ?? createdAt ?? now };
  if (local.on === false) return { on: true, since: now };
  return { on: true, since: createdAt ?? now };
}

/**
 * Primer día (UTC, `YYYY-MM-DD`) que cuenta para las tasas. Instantly filtra por días: si el
 * seguimiento se prendió a media jornada, lo enviado antes ese mismo día no llevaba píxel, así que
 * se empieza al día siguiente.
 */
export function trackingWindowStart(since: Date | string | null | undefined): string | null {
  if (!since) return null;
  const d = new Date(since);
  if (Number.isNaN(d.getTime())) return null;
  const midnight =
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  if (!midnight) day.setUTCDate(day.getUTCDate() + 1);
  return day.toISOString().slice(0, 10);
}

/** Letra de la variante: 0 → "A", 1 → "B"… (Instantly numera desde 0 en analytics). */
export function variantLabel(index: number): string {
  return index >= 0 && index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
}

/** Períodos especiales de `step_metrics`: desde el inicio de la campaña, y "abierto" (hasta hoy). */
export const STEP_PERIOD_ALL_TIME = '2000-01-01';
export const STEP_PERIOD_OPEN_END = '9999-12-31';
